/* tslint:disable */

/* eslint-disable */
import { findChildren } from "@tiptap/core";
import { Fragment, Node as ProsemirrorNode } from "@tiptap/pm/model";
import { EditorState, Plugin, PluginKey, Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import json from "highlight.js/lib/languages/json";
import php from "highlight.js/lib/languages/php";
import plaintext from "highlight.js/lib/languages/plaintext";
import sql from "highlight.js/lib/languages/sql";
import html_xml from "highlight.js/lib/languages/xml";
import yaml from "highlight.js/lib/languages/yaml";
import unescape from "lodash-es/unescape";
import { createLowlight, LanguageFn } from "lowlight";

import { NWVariableAdapter } from "@/components/content/nw-variable/adapter";

type ParsedNode = {
  text: string;
  classes: string[];
};

type Lowlight = ReturnType<typeof createLowlight>;

/**
 * Class responsible for handling syntax highlighting decorations.
 * Based on https://github.com/ueberdosis/tiptap/blob/main/packages/extension-code-block-lowlight.
 */
export class SyntaxHighlighter {
  private name: string;
  private defaultLanguage: string | null | undefined;
  private lowlight: Lowlight;
  private languages: string[] = [];

  constructor(name: string, defaultLanguage: string | null | undefined) {
    this.name = name;
    this.defaultLanguage = defaultLanguage;
    this.lowlight = createLowlight();
    this.installLanguages();
  }

  installLanguages(): void {
    this.registerLanguage("bash", bash);
    this.registerLanguage("shell", bash);
    this.registerLanguage("json", json);
    this.registerLanguage("html", html_xml);
    this.registerLanguage("xml", html_xml);
    this.registerLanguage("yaml", yaml);
    this.registerLanguage("sql", sql);
    this.registerLanguage("php", php);
    this.registerLanguage("text", plaintext);
    this.languages = this.lowlight.listLanguages();
  }

  registerLanguage(language: string, languageDefinition: LanguageFn): void {
    this.lowlight.register(language, languageDefinition);
  }

  highlight(code: string, language: string | undefined): ReturnType<Lowlight["highlight"]>["children"] {
    return language &&
      (this.languages.includes(language) || this.registered(language) || this.lowlight.registered?.(language))
      ? this.getHighlightNodes(this.lowlight.highlight(language, unescape(code)))
      : this.getHighlightNodes(this.lowlight.highlightAuto(unescape(code)));
  }

  /**
   * Creates a Prosemirror plugin for syntax highlighting
   * @returns {Plugin} The created plugin
   */
  public createPlugin(): Plugin<DecorationSet> {
    return new Plugin<DecorationSet>({
      key: new PluginKey("syntax-highlighter"),

      state: {
        init: (_, { doc }): DecorationSet => this.getDecorations(doc),
        apply: (
          transaction: Transaction,
          decorationSet: DecorationSet,
          oldState: EditorState,
          newState: EditorState,
        ): DecorationSet => {
          const oldNodeName = oldState.selection.$head.parent.type.name;
          const newNodeName = newState.selection.$head.parent.type.name;
          const oldNodes = findChildren(oldState.doc, (node: ProsemirrorNode) => node.type.name === this.name);
          const newNodes = findChildren(newState.doc, (node: ProsemirrorNode) => node.type.name === this.name);

          if (
            transaction.docChanged &&
            // Apply decorations if:
            // selection includes named node,
            ([oldNodeName, newNodeName].includes(this.name) ||
              // OR transaction adds/removes named node,
              newNodes.length !== oldNodes.length ||
              // OR transaction has changes that completely encapsulate a node
              // (for example, a transaction that affects the entire document).
              // Such transactions can happen during collab syncing via y-prosemirror, for example.
              transaction.steps.some(step => {
                return (
                  "from" in step &&
                  "to" in step &&
                  oldNodes.some(node => {
                    // Ensure step.from and step.to are numbers before comparison
                    const stepFrom = typeof step.from === "number" ? step.from : 0;
                    const stepTo = typeof step.to === "number" ? step.to : 0;

                    // Check if the node is completely within the step range
                    return node.pos >= stepFrom && node.pos + node.node.nodeSize <= stepTo;
                  })
                );
              }))
          ) {
            return this.getDecorations(transaction.doc);
          }

          return decorationSet.map(transaction.mapping, transaction.doc);
        },
      },

      props: {
        decorations(state: EditorState): DecorationSet | undefined {
          return this.getState(state);
        },
      },
    });
  }

  /**
   * Creates decorations for syntax highlighting
   * @param {ProsemirrorNode} doc - The document to create decorations for
   * @returns {DecorationSet} The created decoration set
   */
  private getDecorations(doc: ProsemirrorNode): DecorationSet {
    const decorations: Decoration[] = [];

    findChildren(doc, (node: ProsemirrorNode) => node.type.name === this.name).forEach(block => {
      let from = block.pos + 1;
      const language = block.node.attrs.language || this.defaultLanguage;

      const text = this.getTextFromFragment(block.node.content);
      const nodes = this.highlight(text, language);

      this.parseNodes(nodes).forEach(node => {
        const to = from + node.text.length;

        if (node.classes.length) {
          const decoration = Decoration.inline(from, to, {
            class: node.classes.join(" "),
          });

          decorations.push(decoration);
        }

        from = to;
      });
    });

    return DecorationSet.create(doc, decorations);
  }

  private getTextFromFragment(fragment: Fragment): string {
    return fragment.content.reduce((acc, node) => {
      if (this.isVariableNode(node)) {
        return (acc += "  ");
      }

      return (acc += node.textContent);
    }, "");
  }

  private isVariableNode(node: ProsemirrorNode): boolean {
    return node.type.name === NWVariableAdapter.name;
  }

  /**
   * Parses nodes recursively to extract text and classes
   * @param {any[]} nodes - The nodes to parse
   * @param {string[]} className - The initial class names
   * @returns {Array<ParsedNode>} Parsed nodes with text and classes
   */
  private parseNodes(nodes: any[], className: string[] = []): ParsedNode[] {
    return nodes
      .map(node => {
        const classes = [...className, ...(node.properties ? node.properties.className : [])];
        if (node.children) {
          return this.parseNodes(node.children, classes);
        }

        return {
          text: node.value,
          classes,
        };
      })
      .flat();
  }

  /**
   * Extracts highlight nodes from a result
   * @param {ReturnType<Lowlight["highlight"]> | ReturnType<Lowlight["highlightAuto"]>} result - The result to extract nodes from
   * @returns {ReturnType<Lowlight["highlight"]>["children"]} The extracted highlight nodes
   */
  private getHighlightNodes(
    result: ReturnType<Lowlight["highlight"]> | ReturnType<Lowlight["highlightAuto"]>,
  ): ReturnType<Lowlight["highlight"]>["children"] {
    return result.children || [];
  }

  /**
   * Checks if a language or alias is registered with highlight.js
   * @param {string} aliasOrLanguage - The language or alias to check
   * @returns {boolean} True if the language is registered, false otherwise
   */
  private registered(aliasOrLanguage: string): boolean {
    return Boolean(hljs.getLanguage(aliasOrLanguage));
  }
}

/**
 * Creates a SyntaxHighlightPlugin
 * @param {Object} params - Parameters for creating the plugin
 * @param {string} params.name - The name of the node type to highlight
 * @param {string | null | undefined} params.defaultLanguage - The default language for highlighting
 * @returns {Plugin} The created syntax highlight plugin
 */
export function SyntaxHighlightPlugin({
  name,
  defaultLanguage,
}: {
  name: string;
  defaultLanguage: string | null | undefined;
}): Plugin<DecorationSet> {
  const highlighter = new SyntaxHighlighter(name, defaultLanguage);
  return highlighter.createPlugin();
}
